import { Box } from '@mui/material';
import { IssueType } from '@pages/QuoteDetails/sidebarItems/underwriting/Underwriting.constants';
import { Table } from '@components/table/Table';
import { useMemo, useState } from 'react';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { useParams } from 'react-router-dom';
import { getUWIssuesColumns } from './getUWIssuesColumns';
import { IActionDialog } from '@pages/QuoteDetails/types/ActionDialog.interface';
import { useQuote } from '@services/quotes';
import { useUnderwritingRules } from '@services/underwritingRules';
import { AddUWIssueDialog } from './AddUWIssueDialog';
import { EditUWIssueDialog } from './EditUWIssueDialog';
import { useUWTasks } from '@services/underwritingTasks/useUWTasks';
import { UnderwritingRuleIds, UnderwritingIssue } from '@models/underwritingIssue';
import { AcceptQuoteDialog } from '@pages/QuoteDetails/dialogs';
import { Action } from '@pages/QuoteDetails/constants/QuoteDetails.constants';
import { underwritingTabStyles } from '@pages/UnderwriterToolkit/tab-content/UnderwritingTab.styles';

export const UnderwritingIssuesPanel = () => {
  const [layoutPreferences] = useStore((state) => [state.layoutPreferences, state.userAuth], shallow);
  const clockFormat = layoutPreferences.clockFormat;
  const { quoteId } = useParams<{ quoteId: string }>();
  const { underwritingTasks, isLoading, isFetching, isError } = useUWTasks(
    { quoteId: quoteId, pageLimit: 100, includeTaskHistory: true },
    { isEnabled: true },
  );
  const [selectedIssue, setSelectedIssue] = useState<IssueType | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [actionDialog, setActionDialog] = useState<IActionDialog>({ show: false, type: null });
  const { quote } = useQuote(quoteId);
  const offeringId = quote?.offering?.id;
  const { underwritingRule } = useUnderwritingRules({ offeringId: offeringId ?? '', pageLimit: 100 });

  const handleEditClick = (selectedIssue: IssueType) => {
    setSelectedIssue(selectedIssue);
    setEditOpen(true);
  };

  const handleRowClick = (row: IssueType) => {
    setSelectedIssue(row);
    setEditOpen(true);
  };

  const columns = getUWIssuesColumns(handleEditClick, clockFormat);

  const issues: IssueType[] = useMemo(() => {
    if (!underwritingTasks?.items) return [];
    const sortFn = (a: UnderwritingIssue, b: UnderwritingIssue) => {
      const sortLogic = { rejected: 1, accepted: 2, open: 3 };
      return (
        sortLogic[a.underwriterDisposition as keyof typeof sortLogic] -
        sortLogic[b.underwriterDisposition as keyof typeof sortLogic]
      );
    };
    const isOtherRule = (id: string) => id === UnderwritingRuleIds.Other;
    return (underwritingTasks.items as UnderwritingIssue[]).sort(sortFn).map((el: UnderwritingIssue) => ({
      status: el.underwriterDisposition || el.status,
      id: el.id,
      ruleId: el.underwritingRule.id,
      description: isOtherRule(el.underwritingRule.id)
        ? `${el.underwritingRule.underwritingLabel} - ${el.underwritingRuleDetail}`
        : el.underwritingRule.underwritingLabel,
      reasoning: el.underwriterComment,
      author: {
        firstName: el.underwriter?.user.firstName,
        lastName: el.underwriter?.user.lastName,
      },
      createdAt: el.createdAt ?? undefined,
      resolvedAt: el.resolvedAt ?? undefined,
      quoteId: el?.quote?.id ?? undefined,
    }));
  }, [underwritingTasks?.items]);

  return (
    <Box sx={underwritingTabStyles.activityPanel}>
      <Box sx={underwritingTabStyles.tableWrapper}>
        <Table
          ariaLabel='Underwriting Issues Table'
          columns={columns}
          onLoadData={() => ({
            data: { items: issues },
            isLoading,
            isFetching,
            isError,
          })}
          rowConfig={{ onRowClick: ({ row }) => handleRowClick(row) }}
          hidePagination
          sxProps={{
            ...underwritingTabStyles,
          }}
        />
      </Box>
      {/* // FULLSCREEN TODO: this button cannot exist if it is always enabled =() */}
      {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}> */}
      {/* <Button
          variant='text'
          onClick={() => setAddDialogOpen(true)}
          startIcon={<Plus color='primary' style={{ height: 16, width: 16 }} />}
        >
          Add New Issue
        </Button>
        {isUnderwriter && <AcceptAllUnderwritingIssuesButton quoteId={quoteId} setActionDialog={setActionDialog} />}
      </Box> */}

      <AddUWIssueDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        underwritingRuleItems={underwritingRule?.items || []}
      />

      <EditUWIssueDialog open={editOpen} onClose={() => setEditOpen(false)} selectedIssue={selectedIssue} />
      {quote && (
        <AcceptQuoteDialog
          isOpen={actionDialog.show && actionDialog.type === Action.AcceptQuote}
          closeDialog={() => setActionDialog({ show: false, type: null })}
          quote={quote}
        />
      )}
    </Box>
  );
};
